import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useState, useEffect } from "react";
import { Heart, MapPin, Users, Calendar, MessageCircle, Plus, Send } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { toast } from "../hooks/use-toast";
import { Link } from "react-router-dom";
import { supabase } from "../../../src/integrations/supabase/client";
import ofuesiLogo from "../assets/ofuesi-logo.png";
import prayerMapNew from "../assets/prayer-map-new.png";
import universityLogosMap from "../assets/university-logos-map.png";
import { US_STATES } from "@/lib/constants";
interface PrayerRequest {
  id: string;
  type: "prayer" | "praise";
  title: string;
  content: string;
  author: string;
  region: string;
  date: string;
}
const PrayerMap = () => {
  const [prayerRequests, setPrayerRequests] = useState<PrayerRequest[]>([]);
  const [regionsData, setRegionsData] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newRequest, setNewRequest] = useState({
    type: "prayer" as "prayer" | "praise",
    title: "",
    content: "",
    author: "",
    email: "",
    state: ""
  });
  const [prayerLetterUrl, setPrayerLetterUrl] = useState<string>(
    "https://ofuesi.org/__l5e/assets-v1/810d3b7c-c76a-4e45-bc00-38cbfa0f4fa0/OFUESI-Prayer-Letter-Oct-2026.pdf"
  );
  useEffect(() => {
    loadData();
  }, [selectedRegion]);
  useEffect(() => {
    supabase
      .from("app_settings")
      .select("value")
      .eq("key", "prayer_letter")
      .maybeSingle()
      .then(({ data }) => {
        const url = (data?.value as any)?.url;
        if (url && typeof url === "string") setPrayerLetterUrl(url);
      });
  }, []);
  const loadData = async () => {
    try {
      const [prayerResult, regionsResult] = await Promise.all([
        (supabase as any).rpc("public_prayer_requests", {
          p_region_id: selectedRegion && selectedRegion !== "all" ? selectedRegion : null,
        }),
        supabase.from("regions").select("*").order("name"),
      ]);
      if (prayerResult.error) throw prayerResult.error;
      if (regionsResult.error) throw regionsResult.error;

      // Convert database prayer points to PrayerRequest format
      const requests = (prayerResult.data || []).map((point: any) => ({
        id: point.id,
        type: "prayer",
        // Default since prayer_requests table doesn't have type field
        title: point.title,
        content: point.description,
        author: point.requester_name || "Anonymous",
        region: regionsResult.data?.find((r: any) => r.id === point.region_id)?.name || "National",
        date: point.created_at
      }));
      setPrayerRequests(requests);
      setRegionsData(regionsResult.data || []);
    } catch (error) {
      console.error("Error loading prayer data:", error);
    }
  };
  const handleSubmitRequest = async () => {
    if (!newRequest.title || !newRequest.content || !newRequest.author || !newRequest.email || !newRequest.state) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields to submit your request.",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      // Find region based on selected state
      const region = regionsData.find(r => r.states?.includes(newRequest.state));
      if (!region) {
        toast({
          title: "Error",
          description: "Could not determine region for selected state.",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      // Create a minimum 2-second delay promise
      const minDelay = new Promise(resolve => setTimeout(resolve, 2000));

      // Submit the actual request
      const submitPromise = (async () => {
        const {
          error
        } = await supabase.from("prayer_requests").insert([{
          title: newRequest.title,
          description: newRequest.content,
          region_id: region.id,
          requester_name: newRequest.author,
          requester_email: newRequest.email,
          status: "active"
        }]);
        if (error) throw error;

        // Send notification to regional coordinators
        try {
          await supabase.functions.invoke("send-prayer-notification", {
            body: {
              requester_name: newRequest.author,
              requester_email: newRequest.email,
              title: newRequest.title,
              description: newRequest.content,
              region_id: region.id
            }
          });
          console.log("Prayer notification sent successfully");
        } catch (notifError) {
          console.error("Failed to send notification:", notifError);
          // Don't fail the request if notification fails
        }
      })();

      // Wait for both the submission and the minimum delay
      await Promise.all([submitPromise, minDelay]);
      setNewRequest({
        type: "prayer",
        title: "",
        content: "",
        author: "",
        email: "",
        state: ""
      });
      toast({
        title: "Request Submitted",
        description: `Your ${newRequest.type} request has been sent to the ${region.name} coordinator.`
      });

      // Reload data to show the new request
      loadData();
    } catch (error) {
      console.error("Error submitting prayer request:", error);
      toast({
        title: "Error",
        description: "Failed to submit your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const regions = regionsData.map(r => r.name);
  const prayerRequestsByRegion = prayerRequests.filter(req => req.type === "prayer");
  const praiseReportsByRegion = prayerRequests.filter(req => req.type === "praise");
  return <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan py-5 relative">
        <div className="absolute inset-0 faith-pattern opacity-20"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto text-white space-y-6">
            <h1 className="text-5xl lg:text-7xl font-bold">OFUESI Praise and Prayer</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              "The harvest is plentiful, but the laborers are few." - Matthew 9:37-38
            </p>
            <p className="text-lg text-blue-200">
              Join us in prayer for university students across United States of America and support for ministry in
              India.
            </p>
            <div className="mt-8 space-y-4">
              <p className="text-xl font-semibold text-white">
                Join Virtual Monthly Prayer Conference (1 hr per month)
              </p>
              <p className="text-lg text-blue-100">Every First Saturday 10:30 AM EST to 11:30 AM EST</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
                <Button asChild size="lg" className="border-white text-white hover:bg-white hover:text-primary hover:border-primary hover:border-2">
                  <a href="https://ofuesi.zoom.us/j/86166516167?pwd=S3ZZZ1pXcS8wMSsxbUVSb0FjZG5Pdz09" target="_blank" rel="noopener noreferrer">
                    Join Zoom Conference
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer">
                  <a href={prayerLetterUrl} target="_blank" rel="noopener noreferrer">
                    Access Prayer Letter
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Prayer Map Reference Image */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">Prayer Map</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              OFUESI's regional coverage across the United States, showing our ministry presence and prayer networks.
            </p>
          </div>
          <div className="space-y-8">
            <Card className="elegant-shadow max-w-6xl mx-auto">
              <CardContent className="p-0">
                <img src={prayerMapNew} alt="OFUESI Prayer Map with regional statistics and prayer points" className="w-full h-auto rounded-lg" />
              </CardContent>
            </Card>
            {/*
             <Card className="elegant-shadow max-w-6xl mx-auto">
              <CardContent className="p-0">
                <img
                  src={universityLogosMap}
                  alt="University logos showing OFUESI's reach across United States of America universities"
                  className="w-full h-auto rounded-lg"
                />
              </CardContent>
             </Card>
             */}
          </div>
        </div>
      </section>

      {/* Prayer Submission Section */}
      <section className="py-12 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="warm-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-center justify-center">
                  <Plus className="w-5 h-5" />
                  <span>Share Prayer Points & Praise Reports</span>
                </CardTitle>
                <p className="text-center text-muted-foreground">
                  Your submissions will be sent directly to regional coordinators for prayer support
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="type">Type</Label>
                  <select id="type" value={newRequest.type} onChange={e => setNewRequest({
                  ...newRequest,
                  type: e.target.value as "prayer" | "praise"
                })} className="w-full mt-1 p-2 border rounded-md" disabled={isSubmitting}>
                    <option value="prayer">Prayer Request</option>
                    <option value="praise">Praise Report</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="state">State</Label>
                  <select id="state" value={newRequest.state} onChange={e => setNewRequest({
                  ...newRequest,
                  state: e.target.value
                })} className="w-full mt-1 p-2 border rounded-md" disabled={isSubmitting}>
                    <option value="">Select your state</option>
                    {US_STATES.map(state => <option key={state} value={state}>
                        {state}
                      </option>)}
                  </select>
                </div>

                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input id="title" value={newRequest.title} onChange={e => setNewRequest({
                  ...newRequest,
                  title: e.target.value
                })} placeholder="Brief title for your request/praise" disabled={isSubmitting} />
                </div>

                <div>
                  <Label htmlFor="author">Your Name</Label>
                  <Input id="author" value={newRequest.author} onChange={e => setNewRequest({
                  ...newRequest,
                  author: e.target.value
                })} placeholder="First name or initials" disabled={isSubmitting} />
                </div>

                <div>
                  <Label htmlFor="email">Your Email</Label>
                  <Input id="email" type="email" value={newRequest.email} onChange={e => setNewRequest({
                  ...newRequest,
                  email: e.target.value
                })} placeholder="your.email@example.com" disabled={isSubmitting} />
                </div>

                <div>
                  <Label htmlFor="content">Details</Label>
                  <Textarea id="content" value={newRequest.content} onChange={e => setNewRequest({
                  ...newRequest,
                  content: e.target.value
                })} placeholder="Share your prayer request or praise report..." rows={4} disabled={isSubmitting} />
                </div>

                <Button onClick={handleSubmitRequest} className="w-full text-white hover:bg-white hover:text-primary hover:border-primary hover:border-2" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <LoadingSpinner size={16} />
                      Submitting...
                    </span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send to Regional Coordinator
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Call to Prayer Section */}
      <section className="py-20 bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto text-white space-y-6">
            <h2 className="text-3xl lg:text-4xl font-bold">Join Our Monthly Prayer Conference</h2>
            <p className="text-xl text-blue-100 leading-relaxed">Commit to praying for university students, ministry leaders, and the work of UESI in India. Your prayers make a difference in lives across two continents. Also remember Students here in USA and OFUESI family as well in your regular prayers </p>
            
          </div>
        </div>
      </section>
    </div>;
};
export default PrayerMap;